import { Link } from 'react-router-dom';
import { useEffect, useRef, useCallback, useState } from 'react';
import SchemaOrg from '../../components/base/SchemaOrg';

// ─── Hero 區域（背景動畫 + 漣漪 + 水柱，全部限制在 Hero 內） ──────
function HeroSection({ children }: { children: React.ReactNode }) {
  const bgCanvasRef = useRef<HTMLCanvasElement>(null);
  const fxCanvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const fxAnimRef = useRef<number>(0);
  const timeRef = useRef<number>(0);
  const sectionRef = useRef<HTMLElement>(null);

  interface Ripple { x: number; y: number; r: number; maxR: number; alpha: number; }
  interface Jet { x: number; y: number; vx: number; vy: number; life: number; maxLife: number; size: number; }

  const ripplesRef = useRef<Ripple[]>([]);
  const jetsRef = useRef<Jet[]>([]);

  // 背景動畫
  useEffect(() => {
    const canvas = bgCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const draw = () => {
      timeRef.current += 0.008;
      const t = timeRef.current;
      const W = canvas.width;
      const H = canvas.height;
      ctx.clearRect(0, 0, W, H);

      const bg = ctx.createLinearGradient(0, 0, 0, H);
      bg.addColorStop(0, '#4a9ec8');
      bg.addColorStop(0.25, '#2e88b8');
      bg.addColorStop(0.5, '#1a72a8');
      bg.addColorStop(0.72, '#0e5e90');
      bg.addColorStop(0.85, '#0a4e7a');
      bg.addColorStop(1, '#063a60');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);

      const sunX = W * 0.82;
      const sunY = H * 0.12;
      const sunGrad = ctx.createRadialGradient(sunX, sunY, 0, sunX, sunY, W * 0.35);
      sunGrad.addColorStop(0, `rgba(255,255,220,${0.55 + Math.sin(t * 0.3) * 0.05})`);
      sunGrad.addColorStop(0.2, 'rgba(255,240,180,0.25)');
      sunGrad.addColorStop(0.5, 'rgba(255,220,120,0.10)');
      sunGrad.addColorStop(1, 'rgba(255,200,80,0)');
      ctx.fillStyle = sunGrad;
      ctx.fillRect(0, 0, W, H);

      ctx.beginPath();
      ctx.arc(sunX, sunY, 38 + Math.sin(t * 0.5) * 2, 0, Math.PI * 2);
      const sunCore = ctx.createRadialGradient(sunX, sunY, 0, sunX, sunY, 38);
      sunCore.addColorStop(0, 'rgba(255,255,200,0.95)');
      sunCore.addColorStop(0.6, 'rgba(255,240,150,0.7)');
      sunCore.addColorStop(1, 'rgba(255,220,100,0)');
      ctx.fillStyle = sunCore;
      ctx.fill();

      for (let i = 0; i < 12; i++) {
        const rayAngle = (i / 12) * Math.PI * 2 + t * 0.15;
        const rayLen = 60 + Math.sin(t * 0.8 + i) * 20;
        ctx.beginPath();
        ctx.moveTo(sunX + Math.cos(rayAngle) * 42, sunY + Math.sin(rayAngle) * 42);
        ctx.lineTo(sunX + Math.cos(rayAngle) * (42 + rayLen), sunY + Math.sin(rayAngle) * (42 + rayLen));
        ctx.strokeStyle = `rgba(255,245,180,${0.25 + Math.sin(t + i) * 0.08})`;
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      for (let i = 0; i < 18; i++) {
        const glintX = W * (0.05 + (i / 18) * 0.9) + Math.sin(t * 0.7 + i * 1.3) * 30;
        const glintY = H * (0.62 + Math.sin(t * 0.5 + i * 0.8) * 0.06);
        const glintW = 15 + Math.sin(t * 1.2 + i) * 10;
        const glintOpacity = 0.15 + Math.sin(t * 1.5 + i * 0.9) * 0.1;
        ctx.beginPath();
        ctx.ellipse(glintX, glintY, glintW, 3, Math.sin(t * 0.3 + i) * 0.3, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${glintOpacity})`;
        ctx.fill();
      }

      const waveLayers = [
        { yRatio: 0.55, amp: 22, freq: 2.0, speed: 0.6,  alpha: 0.18, r: 30,  g: 160, b: 220 },
        { yRatio: 0.62, amp: 18, freq: 2.8, speed: -0.8, alpha: 0.20, r: 50,  g: 180, b: 235 },
        { yRatio: 0.69, amp: 15, freq: 3.5, speed: 1.0,  alpha: 0.22, r: 80,  g: 200, b: 245 },
        { yRatio: 0.76, amp: 12, freq: 4.2, speed: -1.2, alpha: 0.25, r: 120, g: 215, b: 250 },
        { yRatio: 0.83, amp: 9,  freq: 5.5, speed: 1.4,  alpha: 0.28, r: 160, g: 230, b: 255 },
        { yRatio: 0.90, amp: 6,  freq: 7.0, speed: -0.9, alpha: 0.30, r: 200, g: 242, b: 255 },
        { yRatio: 0.96, amp: 4,  freq: 9.0, speed: 1.1,  alpha: 0.35, r: 230, g: 250, b: 255 },
      ];

      waveLayers.forEach(layer => {
        ctx.beginPath();
        for (let x = 0; x <= W; x += 3) {
          const y = H * layer.yRatio
            + Math.sin((x / W) * Math.PI * layer.freq + t * layer.speed) * layer.amp
            + Math.sin((x / W) * Math.PI * (layer.freq * 1.7) + t * layer.speed * 1.3) * (layer.amp * 0.4)
            + Math.cos((x / W) * Math.PI * (layer.freq * 0.9) + t * layer.speed * 0.6) * (layer.amp * 0.2);
          x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.lineTo(W, H); ctx.lineTo(0, H); ctx.closePath();
        ctx.fillStyle = `rgba(${layer.r},${layer.g},${layer.b},${layer.alpha})`;
        ctx.fill();
      });

      waveLayers.slice(0, 4).forEach(layer => {
        ctx.beginPath();
        for (let x = 0; x <= W; x += 3) {
          const y = H * layer.yRatio
            + Math.sin((x / W) * Math.PI * layer.freq + t * layer.speed) * layer.amp
            + Math.sin((x / W) * Math.PI * (layer.freq * 1.7) + t * layer.speed * 1.3) * (layer.amp * 0.4);
          x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.strokeStyle = `rgba(255,255,255,${layer.alpha * 1.8})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      });

      for (let i = 0; i < 6; i++) {
        const bx = W * (0.08 + i * 0.16) + Math.sin(t * 0.4 + i * 1.5) * 30;
        const by = H * (0.14 + Math.sin(t * 0.3 + i * 0.9) * 0.05);
        const wingSpan = 20 + i * 4;
        const flapAngle = Math.sin(t * 2.8 + i) * 0.45;
        const bodySize = 4 + i * 0.5;

        ctx.beginPath();
        ctx.moveTo(bx - wingSpan, by + Math.sin(flapAngle) * 8);
        ctx.quadraticCurveTo(bx - wingSpan * 0.5, by - 8, bx, by);
        ctx.quadraticCurveTo(bx + wingSpan * 0.5, by - 8, bx + wingSpan, by + Math.sin(flapAngle) * 8);
        ctx.strokeStyle = `rgba(255,255,255,${0.85 + i * 0.02})`;
        ctx.lineWidth = 2.5;
        ctx.lineJoin = 'round';
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(bx - wingSpan, by + Math.sin(flapAngle) * 8);
        ctx.quadraticCurveTo(bx - wingSpan * 0.5, by - 8, bx, by);
        ctx.quadraticCurveTo(bx + wingSpan * 0.5, by - 8, bx + wingSpan, by + Math.sin(flapAngle) * 8);
        ctx.quadraticCurveTo(bx + wingSpan * 0.5, by + 4, bx, by + bodySize);
        ctx.quadraticCurveTo(bx - wingSpan * 0.5, by + 4, bx - wingSpan, by + Math.sin(flapAngle) * 8);
        ctx.fillStyle = 'rgba(255,255,255,0.25)';
        ctx.fill();

        ctx.beginPath();
        ctx.ellipse(bx, by + bodySize * 0.5, bodySize * 0.6, bodySize * 0.4, 0, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,255,255,0.7)';
        ctx.fill();
      }

      animRef.current = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  // 漣漪 + 水柱特效（限制在 Hero 區域）
  useEffect(() => {
    const canvas = fxCanvasRef.current;
    const section = sectionRef.current;
    if (!canvas || !section) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = section.offsetWidth;
      canvas.height = section.offsetHeight;
    };
    resize();

    const spawnJets = (x: number, y: number, count: number) => {
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 2 + Math.random() * 4;
        jetsRef.current.push({
          x, y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed - Math.random() * 3,
          life: 0,
          maxLife: 40 + Math.random() * 30,
          size: 2 + Math.random() * 3,
        });
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      const rect = section.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      ripplesRef.current.push({ x, y, r: 0, maxR: 60 + Math.random() * 40, alpha: 0.5 });
      if (Math.random() < 0.3) spawnJets(x, y, 4);
    };

    const handleClick = (e: MouseEvent) => {
      // 如果點擊的是連結元素，不攔截，讓連結正常跳轉
      const target = e.target as HTMLElement;
      if (target.closest('a')) return;

      const rect = section.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      ripplesRef.current.push({ x, y, r: 0, maxR: 120, alpha: 0.7 });
      ripplesRef.current.push({ x, y, r: 0, maxR: 80, alpha: 0.5 });
      spawnJets(x, y, 18);
    };

    section.addEventListener('mousemove', handleMouseMove);
    section.addEventListener('click', handleClick);

    const drawFx = () => {
      canvas.width = section.offsetWidth;
      canvas.height = section.offsetHeight;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // 漣漪
      ripplesRef.current = ripplesRef.current.filter(rp => rp.alpha > 0.01);
      ripplesRef.current.forEach(rp => {
        rp.r += 2.5;
        rp.alpha *= 0.93;
        ctx.beginPath();
        ctx.arc(rp.x, rp.y, rp.r, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(180,230,255,${rp.alpha})`;
        ctx.lineWidth = 2;
        ctx.stroke();

        if (rp.r > rp.maxR * 0.5) {
          ctx.beginPath();
          ctx.arc(rp.x, rp.y, rp.r * 0.6, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(255,255,255,${rp.alpha * 0.4})`;
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      });

      // 水柱粒子
      jetsRef.current = jetsRef.current.filter(j => j.life < j.maxLife);
      jetsRef.current.forEach(j => {
        j.x += j.vx;
        j.y += j.vy;
        j.vy += 0.15;
        j.life++;
        const progress = j.life / j.maxLife;
        const alpha = (1 - progress) * 0.7;
        const size = j.size * (1 - progress * 0.5);
        ctx.beginPath();
        ctx.arc(j.x, j.y, size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(180,230,255,${alpha})`;
        ctx.fill();
      });

      fxAnimRef.current = requestAnimationFrame(drawFx);
    };
    drawFx();

    return () => {
      cancelAnimationFrame(fxAnimRef.current);
      section.removeEventListener('mousemove', handleMouseMove);
      section.removeEventListener('click', handleClick);
    };
  }, []);

  return (
    <section ref={sectionRef} className="relative pt-32 pb-24 overflow-hidden min-h-[680px]">
      {/* 背景動畫 */}
      <canvas ref={bgCanvasRef} className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }} />
      {/* 漣漪水柱特效層 */}
      <canvas ref={fxCanvasRef} className="absolute inset-0 w-full h-full z-[3]" style={{ pointerEvents: 'none' }} />

      {/* 頂部暗化遮罩 */}
      <div className="absolute inset-0 bg-gradient-to-b from-sky-950/50 via-sky-900/20 to-sky-950/55 pointer-events-none z-[1]" />
      {/* 底部漸層 */}
      <div className="absolute bottom-0 left-0 right-0 h-36 pointer-events-none z-[2]"
        style={{ background: 'linear-gradient(to top, rgba(220,240,255,0.7), transparent)' }} />

      {children}
    </section>
  );
}

// ─── 數字滾動動畫 ──────────────────────────────────────────────────
function CountUp({ target, suffix = '', duration = 1800 }: { target: number; suffix?: string; duration?: number }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && !started.current) {
        started.current = true;
        const start = performance.now();
        const tick = (now: number) => {
          const progress = Math.min((now - start) / duration, 1);
          const ease = 1 - Math.pow(1 - progress, 3);
          setCount(Math.floor(ease * target));
          if (progress < 1) requestAnimationFrame(tick);
          else setCount(target);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.5 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [target, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
}

// ─── 服務卡片懸停光暈 ──────────────────────────────────────────────
function ServiceCard({ icon, title, desc, link }: { icon: string; title: string; desc: string; link: string }) {
  const [hovered, setHovered] = useState(false);
  const [pos, setPos] = useState({ x: 50, y: 50 });

  const handleMouseMove = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPos({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  };

  return (
    <Link
      to={link}
      className="relative overflow-hidden bg-white/20 backdrop-blur-md border border-white/40 rounded-2xl p-6 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-sky-400/30 hover:border-sky-300/60 cursor-pointer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onMouseMove={handleMouseMove}
    >
      {hovered && (
        <div
          className="absolute inset-0 pointer-events-none transition-opacity duration-300"
          style={{
            background: `radial-gradient(circle at ${pos.x}% ${pos.y}%, rgba(80,190,255,0.22) 0%, transparent 65%)`,
          }}
        />
      )}
      <div className="relative z-10">
        <div className="w-16 h-16 bg-white/30 border border-white/50 rounded-full flex items-center justify-center mb-4 mx-auto">
          <i className={`${icon} text-3xl text-white drop-shadow`}></i>
        </div>
        <h4 className="text-xl font-bold text-white mb-2 text-center drop-shadow">{title}</h4>
        <p className="text-sm text-white/80 text-center leading-relaxed">{desc}</p>
      </div>
    </Link>
  );
}

// ─── 主頁面 ────────────────────────────────────────────────────────
export default function HomePage() {
  const [activeFeature, setActiveFeature] = useState(0);
  const [line1Text, setLine1Text] = useState('');
  const [line2Text, setLine2Text] = useState('');
  const [showCursor1, setShowCursor1] = useState(true);
  const [showCursor2, setShowCursor2] = useState(false);
  const [typingDone, setTypingDone] = useState(false);

  const LINE1 = '專業管道清潔';
  const LINE2 = '守護您的用水安全';

  useEffect(() => {
    let i = 0;
    setLine1Text('');
    setLine2Text('');
    setShowCursor1(true);
    setShowCursor2(false);
    setTypingDone(false);

    const type1 = setInterval(() => {
      i++;
      setLine1Text(LINE1.slice(0, i));
      if (i >= LINE1.length) {
        clearInterval(type1);
        setShowCursor1(false);
        setShowCursor2(true);
        let j = 0;
        const type2 = setInterval(() => {
          j++;
          setLine2Text(LINE2.slice(0, j));
          if (j >= LINE2.length) {
            clearInterval(type2);
            setTypingDone(true);
          }
        }, 80);
      }
    }, 100);

    return () => clearInterval(type1);
  }, []);

  const features = [
    { icon: 'ri-shield-check-line', title: '政府認證合格', desc: '持有環保署認可清潔業執照，所有作業符合政府法規標準，讓您安心委託。', color: 'cyan' },
    { icon: 'ri-user-star-line', title: '專業人員施工', desc: '全程由持證技術人員親自操作，嚴格遵循施工規範，確保每道工序精準到位、品質穩定可靠。', color: 'teal' },
    { icon: 'ri-time-line', title: '24小時快速到府', desc: '緊急管道堵塞？我們全年無休，最快2小時內到達現場處理。', color: 'sky' },
    { icon: 'ri-award-line', title: '20年專業經驗', desc: '超過5000件成功案例，服務桃園、新北、台北、新竹、苗栗等地區。', color: 'blue' },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % features.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [features.length]);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': `${siteUrl}/#organization`,
      name: '永業環保',
      description: '專業環保清潔服務，提供水塔清洗、水管疏通、水溝清理、內視鏡檢查等服務。20年經驗，5000+成功案例，24小時快速到府。',
      url: siteUrl,
      telephone: '0800-281-281',
      priceRange: '$$',
      address: {
        '@type': 'PostalAddress',
        addressRegion: '桃園市',
        addressCountry: 'TW'
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 24.99362,
        longitude: 121.22307
      },
      areaServed: [
        { '@type': 'City', name: '桃園市' },
        { '@type': 'City', name: '新北市' },
        { '@type': 'City', name: '台北市' },
        { '@type': 'City', name: '新竹市' },
        { '@type': 'City', name: '苗栗縣' }
      ],
      openingHoursSpecification: {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
        opens: '00:00',
        closes: '23:59'
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '500',
        bestRating: '5',
        worstRating: '1'
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`,
      url: siteUrl,
      name: '永業環保 - 專業環保清潔服務',
      description: '提供水塔清洗、水管疏通、水溝清理、內視鏡檢查等專業清潔服務',
      publisher: {
        '@id': `${siteUrl}/#organization`
      }
    }
  ];

  return (
    <>
      <SchemaOrg schema={schemas} />
      {/* ── Hero ── */}
      <HeroSection>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          {/* 標籤 */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 bg-white/25 border border-white/50 text-white px-5 py-2 rounded-full text-sm font-medium backdrop-blur-sm">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-water-flash-line text-sm drop-shadow"></i>
              </div>
              <span className="drop-shadow font-semibold">高壓水刀 · 專業清潔 · 守護管道健康</span>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-drop-line text-sm drop-shadow"></i>
              </div>
            </div>
          </div>

          {/* 主標題 */}
          <div className="text-center mb-10">
            <style>{`
              @keyframes cursorBlink {
                0%, 100% { opacity: 1; }
                50% { opacity: 0; }
              }
              @keyframes charFlash {
                0% { opacity: 0; transform: scale(1.3); }
                60% { opacity: 1; transform: scale(1.05); }
                100% { opacity: 1; transform: scale(1); }
              }
              .typing-char {
                display: inline-block;
                animation: charFlash 0.25s ease forwards;
              }
            `}</style>

            {/* 第一行 */}
            <h2 className="text-4xl md:text-6xl font-bold mb-3 text-white drop-shadow-lg leading-tight min-h-[1.2em]">
              {line1Text.split('').map((char, idx) => (
                <span key={idx} className="typing-char">{char}</span>
              ))}
              {showCursor1 && (
                <span
                  style={{
                    display: 'inline-block',
                    width: '3px',
                    height: '0.85em',
                    background: 'rgba(255,255,255,0.9)',
                    borderRadius: '2px',
                    marginLeft: '4px',
                    verticalAlign: 'middle',
                    animation: 'cursorBlink 0.8s step-end infinite',
                  }}
                />
              )}
            </h2>

            {/* 第二行 */}
            <h3
              className="text-5xl md:text-7xl font-bold mb-5 drop-shadow-lg leading-tight min-h-[1.2em]"
            >
              {line2Text.split('').map((char, idx) => (
                <span
                  key={idx}
                  className="typing-char"
                  style={{
                    display: 'inline-block',
                    background: 'linear-gradient(135deg, #ffffff, #b8e8ff, #60c8f0, #1e90d8)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    filter: 'drop-shadow(0 2px 8px rgba(0,100,200,0.3))',
                  }}
                >
                  {char}
                </span>
              ))}
              {(showCursor2 || typingDone) && (
                <span
                  style={{
                    display: 'inline-block',
                    width: '3px',
                    height: '0.85em',
                    background: 'linear-gradient(180deg, #ffffff, #60c8f0)',
                    borderRadius: '2px',
                    marginLeft: '6px',
                    verticalAlign: 'middle',
                    animation: 'cursorBlink 1.1s step-end infinite',
                  }}
                />
              )}
            </h3>

            <p className="text-lg md:text-2xl text-white/90 font-medium mb-2 drop-shadow">
              20年經驗 · 5000+ 成功案例 · 值得信賴
            </p>
          </div>

          {/* 按鈕 */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 mb-14">
            <a href="tel:0800-281-281"
              className="bg-sky-500 text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-sky-400 transition-all hover:scale-105 flex items-center justify-center gap-2 whitespace-nowrap w-full md:w-auto shadow-lg shadow-sky-500/40">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-line text-lg"></i>
              </div>
              立即預約 0800-281-281
            </a>
            <a href="tel:0906629777"
              className="bg-red-500/80 backdrop-blur-sm text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-red-500 transition-all hover:scale-105 flex items-center justify-center gap-2 whitespace-nowrap w-full md:w-auto shadow-lg shadow-red-500/30">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-alarm-warning-line text-lg"></i>
              </div>
              緊急專線 0906-629-777
            </a>
            <Link to="/contact"
              className="bg-white/20 backdrop-blur-sm text-white border-2 border-white/50 px-8 py-4 rounded-full text-lg font-medium hover:bg-white/35 transition-all hover:scale-105 flex items-center justify-center gap-2 whitespace-nowrap w-full md:w-auto">
              免費諮詢
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line text-lg"></i>
              </div>
            </Link>
          </div>

          {/* 服務卡片 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5 max-w-6xl mx-auto">
            {[
              { icon: 'ri-water-flash-line', title: '水塔清洗', desc: '專業清洗消毒，確保水質純淨安全', link: '/services/water-tank' },
              { icon: 'ri-tools-line', title: '水管疏通', desc: '高壓水刀技術，不傷管道快速疏通', link: '/services/pipe-cleaning' },
              { icon: 'ri-road-map-line', title: '水溝清理', desc: '徹底清除淤積，恢復排水順暢', link: '/services/drain-cleaning' },
              { icon: 'ri-search-eye-line', title: '內視鏡檢查', desc: '精準定位問題，對症下藥處理', link: '/services/endoscope' },
            ].map((s, i) => (
              <ServiceCard key={i} {...s} />
            ))}
          </div>
        </div>
      </HeroSection>

      {/* ── 服務項目 ── */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <p className="text-sm text-gray-500 font-light mb-2 tracking-widest">OUR SERVICES</p>
            <h2 className="text-5xl font-bold text-gray-900 mb-2">專業清潔服務</h2>
            <h3 className="text-4xl font-serif italic text-[#1e3a5f]">為您量身打造</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { title: '水塔清洗', desc: '定期清洗消毒，去除水垢與雜質', color: 'bg-sky-50', image: 'https://static.readdy.ai/image/c61c43d626627dd077148c0c867066d5/3dd2f5d595a386c624120fabaece0a7c.png', link: '/services/water-tank' },
              { title: '水管水刀疏通', desc: '高壓水刀技術，不傷管道快速疏通', color: 'bg-cyan-50', image: 'https://static.readdy.ai/image/c61c43d626627dd077148c0c867066d5/5e9098a6f6e994c42153a718564101a9.png', link: '/services/pipe-cleaning' },
              { title: '水溝疏通', desc: '清除淤積物，恢復排水順暢', color: 'bg-gray-50', image: 'https://static.readdy.ai/image/c61c43d626627dd077148c0c867066d5/2c5c056fe5e87ac4b51420683fef73c8.png', link: '/services/drain-cleaning' },
              { title: '內視鏡檢查', desc: '精準檢測管道內部狀況，找出問題根源', color: 'bg-amber-50', image: 'https://public.readdy.ai/ai/img_res/05df2b418b1dc257aaf93bd7d8771ebc.jpg', link: '/services/endoscope' },
            ].map((service, index) => (
              <Link key={index} to={service.link} className="rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2 cursor-pointer">
                <div className={`${service.color} h-80 flex items-center justify-center overflow-hidden`}>
                  <img src={service.image} alt={service.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="bg-white p-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h4>
                  <p className="text-sm text-gray-600 mb-3">{service.desc}</p>
                  <span className="text-[#1e3a5f] text-sm font-medium flex items-center gap-1 hover:gap-2 transition-all whitespace-nowrap">
                    了解更多
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-lg"></i>
                    </div>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── 互動特色區 ── */}
      <section className="py-20 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #b8d8f0 0%, #8ec4e8 40%, #6ab0d8 100%)' }}>
        <div className="absolute inset-0 pointer-events-none opacity-30">
          {[0,1,2].map(i => (
            <div key={i} className="absolute w-full h-px"
              style={{
                top: `${30 + i * 25}%`,
                background: 'linear-gradient(90deg, transparent, rgba(30,140,220,0.5), transparent)',
              }} />
          ))}
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-14">
            <p className="text-sky-500/70 text-sm tracking-widest mb-2">WHY CHOOSE US</p>
            <h2 className="text-4xl md:text-5xl font-bold text-sky-950 mb-3">選擇永業環保的理由</h2>
            <p className="text-sky-800/70 text-base">點擊了解每項優勢的詳細說明</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="space-y-4">
              {features.map((f, i) => (
                <button
                  key={i}
                  onClick={() => setActiveFeature(i)}
                  className={`w-full text-left p-5 rounded-2xl border transition-all duration-300 cursor-pointer ${
                    activeFeature === i
                      ? 'bg-sky-600/20 border-sky-500/60 shadow-lg shadow-sky-400/20'
                      : 'bg-white/70 border-sky-300/60 hover:bg-white/90 hover:border-sky-400/70'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 flex items-center justify-center rounded-full shrink-0 transition-all duration-300 ${
                      activeFeature === i ? 'bg-sky-600/25 border border-sky-500/60' : 'bg-sky-200/80'
                    }`}>
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className={`${f.icon} text-xl ${activeFeature === i ? 'text-sky-700' : 'text-sky-600'}`}></i>
                      </div>
                    </div>
                    <div>
                      <h4 className={`font-bold text-lg transition-colors ${activeFeature === i ? 'text-sky-800' : 'text-sky-950/80'}`}>
                        {f.title}
                      </h4>
                      {activeFeature === i && (
                        <p className="text-sky-900/70 text-sm mt-1 leading-relaxed">{f.desc}</p>
                      )}
                    </div>
                    <div className="ml-auto w-6 h-6 flex items-center justify-center">
                      <i className={`ri-arrow-right-s-line text-xl transition-all duration-300 ${activeFeature === i ? 'text-sky-500 rotate-90' : 'text-sky-300'}`}></i>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-5">
              {[
                { target: 5000, suffix: '+', label: '服務案例', icon: 'ri-briefcase-line' },
                { target: 98, suffix: '%', label: '客戶滿意度', icon: 'ri-emotion-happy-line' },
                { target: 20, suffix: '年', label: '專業經驗', icon: 'ri-medal-line' },
                { target: 24, suffix: 'H', label: '快速到府', icon: 'ri-time-line' },
              ].map((stat, i) => (
                <div key={i} className="bg-white/80 border border-sky-300/70 rounded-2xl p-6 text-center hover:bg-white hover:border-sky-500/60 transition-all duration-300 hover:-translate-y-1 cursor-default shadow-sm">
                  <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3">
                    <i className={`${stat.icon} text-2xl text-sky-600`}></i>
                  </div>
                  <div className="text-4xl md:text-5xl font-bold text-sky-900 mb-1">
                    <CountUp target={stat.target} suffix={stat.suffix} />
                  </div>
                  <p className="text-sky-700/80 text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── 服務價目表 ── */}
      <section id="pricing" className="py-20 bg-[#faf8f5]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-sm text-gray-500 tracking-widest mb-2">PRICING</p>
            <h2 className="text-5xl font-bold text-gray-900 mb-3">服務價目表</h2>
            <p className="text-gray-500 text-base">透明收費，無隱藏費用</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
            {[
              { icon: 'ri-water-flash-line', title: '水塔清洗', price: 'NT$ 2,500 ～ 5,000', desc: '依水塔容量與污染程度計費', color: 'bg-sky-50', accent: 'text-sky-700', border: 'border-sky-100' },
              { icon: 'ri-tools-line', title: '水管水刀疏通', price: 'NT$ 15,000 起', desc: '依管線長度與堵塞狀況計費', color: 'bg-cyan-50', accent: 'text-cyan-700', border: 'border-cyan-100' },
              { icon: 'ri-search-eye-line', title: '內視鏡檢修', price: 'NT$ 8,000', desc: '管道內部精密攝影檢查', color: 'bg-amber-50', accent: 'text-amber-700', border: 'border-amber-100' },
              { icon: 'ri-road-map-line', title: '水溝疏通', price: 'NT$ 20,000 起', desc: '依水溝長度與淤積程度計費', color: 'bg-teal-50', accent: 'text-teal-700', border: 'border-teal-100' },
            ].map((item, index) => (
              <div key={index} className={`${item.color} border ${item.border} rounded-2xl p-7 flex items-center gap-6 hover:shadow-md transition-all cursor-default`}>
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-white shadow-sm shrink-0">
                  <i className={`${item.icon} text-2xl ${item.accent}`}></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-bold text-gray-900 mb-1">{item.title}</h4>
                  <p className="text-sm text-gray-500 mb-2">{item.desc}</p>
                  <span className={`text-xl font-bold ${item.accent}`}>{item.price}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="bg-[#1e3a5f] rounded-2xl p-6 flex md:flex-row flex-col items-center gap-6 mb-6">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-white/10 shrink-0">
              <i className="ri-car-line text-2xl text-white"></i>
            </div>
            <div className="flex-1 md:text-left text-center">
              <h4 className="text-lg font-bold text-white mb-1">場勘出車費</h4>
              <p className="text-sm text-blue-200">到府現場評估勘查費用</p>
            </div>
            <span className="text-2xl font-bold text-white whitespace-nowrap">NT$ 3,000</span>
          </div>
          <div className="flex items-start gap-3 bg-white border border-gray-200 rounded-xl px-6 py-4">
            <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-information-line text-lg text-gray-400"></i>
            </div>
            <p className="text-sm text-gray-500 leading-relaxed">
              以上價格僅供參考，<strong className="text-gray-700">實際價格依現場評估為準</strong>。不同案場條件、管線長度、污染程度皆可能影響最終報價，歡迎來電或填寫表單，我們將安排專人免費諮詢。
            </p>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section id="booking" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h2 className="text-5xl font-bold text-gray-900 mb-4">立即預約專業清潔服務</h2>
          <p className="text-xl text-gray-600 mb-8">免費到府估價 | 當日快速服務 | 滿意再付款</p>
          <a href="tel:0800-281-281" className="inline-flex items-center justify-center gap-2 bg-[#6b4423] text-white px-10 py-4 rounded-full text-lg font-medium hover:bg-[#8b5a33] transition-all hover:scale-105 mb-16 whitespace-nowrap w-full md:w-auto">
            立即撥打 0800-281-281
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-right-line text-lg"></i>
            </div>
          </a>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              'https://readdy.ai/api/search-image?query=professional%20high%20pressure%20water%20jet%20cleaning%20equipment%20modern%20tool%20clean%20white%20background%20product%20photography%20high%20quality%20detailed&width=300&height=300&seq=equipment1&orientation=squarish',
              'https://readdy.ai/api/search-image?query=professional%20endoscope%20inspection%20camera%20equipment%20modern%20tool%20clean%20white%20background%20product%20photography%20high%20quality%20detailed&width=300&height=300&seq=equipment2&orientation=squarish',
              'https://readdy.ai/api/search-image?query=professional%20pipe%20cleaning%20tools%20equipment%20set%20modern%20clean%20white%20background%20product%20photography%20high%20quality%20detailed&width=300&height=300&seq=equipment3&orientation=squarish',
              'https://readdy.ai/api/search-image?query=professional%20water%20tank%20cleaning%20equipment%20modern%20tool%20clean%20white%20background%20product%20photography%20high%20quality%20detailed&width=300&height=300&seq=equipment4&orientation=squarish',
            ].map((img, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-md">
                <img src={img} alt={`設備 ${index + 1}`} className="w-full h-48 object-contain" />
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
